
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { ChevronDown, Play, Camera, Laptop, Smartphone } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { useLanguage } from '@/hooks/useLanguage';


interface HeroSliderProps {
  onExploreClick: () => void;
  headerAnimationComplete: boolean;
}





const HeroSlider = ({ onExploreClick, headerAnimationComplete }: HeroSliderProps) => {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const [imageLoadErrors, setImageLoadErrors] = useState<Record<string, boolean>>({});
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const carouselRef = useRef<any>(null);
  const autoRotateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Video sources - using a reliable source
  const videoSrc = "/sources/header/start.mp4";
  
  // Background images that should be preloaded
  const backgroundImages = [
    "https://images.unsplash.com/photo-1533421644343-45b606745fb1?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80",
    "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80",
    "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80"
  ];

  // Preload background images
  useEffect(() => {
    const imagePromises = backgroundImages.map((src, index) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          resolve(src);
          console.log(`Image ${index} loaded successfully:`, src);
        };
        img.onerror = () => {
          setImageLoadErrors(prev => ({ ...prev, [src]: true }));
          reject(new Error(`Failed to load image: ${src}`));
        };
      });
    });

    Promise.allSettled(imagePromises).then((results) => {
      console.log('Image preloading complete', results);
      setImagesPreloaded(true);
    });
  }, []);

  // Set up auto-rotation
  useEffect(() => {
    if (autoRotateIntervalRef.current) {
      clearInterval(autoRotateIntervalRef.current);
    }

    autoRotateIntervalRef.current = setInterval(() => {
      if (carouselRef.current?.api) {
        carouselRef.current.api.scrollNext();
      }
    }, 10000); // Rotate every 10 seconds

    return () => {
      if (autoRotateIntervalRef.current) {
        clearInterval(autoRotateIntervalRef.current);
      }
    };
  }, []);

  // Handle manual slide change
  const handleSlideChange = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  // Pre-load the video
  useEffect(() => {
    const videoElement = document.createElement('video');
    videoElement.muted = true;
    videoElement.src = videoSrc;
    videoElement.addEventListener('loadeddata', () => setVideoLoaded(true));
    videoElement.addEventListener('error', () => setVideoError(true));
    
    return () => {
      videoElement.pause();
      videoElement.src = '';
      videoElement.removeEventListener('loadeddata', () => setVideoLoaded(true));
      videoElement.removeEventListener('error', () => setVideoError(true));
    };
  }, []);

  // Handle carousel API
  const handleCarouselApi = useCallback((api: any) => {
    carouselRef.current = { api };
    
    api.on('select', () => {
      setActiveIndex(api.selectedScrollSnap());
    });
  }, []);

  // Use a fallback image in case of loading errors
  const getFallbackImage = (index: number) => {
    // Generic fallback images
    const fallbacks = [
      "https://images.unsplash.com/photo-1549492423-94aead85e950?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80",
      "https://images.unsplash.com/photo-1601987177651-8edfe6c20009?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80",
      "https://images.unsplash.com/photo-1610123598195-eea6b6be4c48?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80"
    ];
    return fallbacks[index % fallbacks.length];
  };

  if (!imagesPreloaded) {
    // Show a loading state
    return (
      <div className="relative h-[100vh] flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="relative h-[100vh] overflow-hidden">
      <Carousel 
        className="h-full" 
        setApi={handleCarouselApi}
        opts={{
          loop: true,
          align: "start",
        }}
      >
        <CarouselContent className="h-full">
          {/* Video Slide */}
          <CarouselItem className="h-full">
            <div className="relative h-full flex items-center justify-center">
              <div className="absolute inset-0 z-0">
                {videoError ? (
                  <img 
                    src={imageLoadErrors[backgroundImages[0]] 
                      ? getFallbackImage(0) 
                      : backgroundImages[0]
                    }
                    alt="Filmmaker background"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                    poster={imageLoadErrors[backgroundImages[0]] 
                      ? getFallbackImage(0) 
                      : backgroundImages[0]
                    }
                  >
                    <source src={videoSrc} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}
                <div className="absolute inset-0 bg-black/50"></div>
              </div>

              <div className="relative z-10 text-center text-white px-6">
                <div className="mb-6 inline-flex items-center justify-center bg-primary/20 text-primary rounded-full p-3">
                  <Play className="h-8 w-8 fill-current" />
                </div>
                <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">{t('Visual Stories')}</h1>
                <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto animate-fade-in" style={{animationDelay: "0.2s"}}>
                  {t('Capturing life\'s most beautiful moments through professional videography')}
                </p>
                <Button 
                  onClick={onExploreClick}
                  className="animate-fade-in"
                  style={{animationDelay: "0.4s"}}
                  size="lg"
                >
                  {t('Explore My Work')}
                </Button>
              </div>
            </div>
          </CarouselItem>

          {/* Photo Slide */}
          <CarouselItem className="h-full">
            <div className="relative h-full flex items-center justify-center">
              <div className="absolute inset-0 z-0">
                <img 
                  src={imageLoadErrors[backgroundImages[1]] 
                    ? getFallbackImage(1) 
                    : backgroundImages[1]
                  }
                  alt="Photography background"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50"></div>
              </div>

              <div className="relative z-10 text-center text-white px-6">
                <div className="mb-6 inline-flex items-center justify-center bg-primary/20 text-primary rounded-full p-3">
                  <Camera className="h-8 w-8" />
                </div>
                <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
                  {t('Stunning Photography')}
                </h1>
                <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto animate-fade-in" style={{animationDelay: "0.2s"}}>
                  {t('Professional photography that captures the essence of your brand')}
                </p>
                <Button 
                  onClick={onExploreClick}
                  className="animate-fade-in"
                  style={{animationDelay: "0.4s"}}
                  size="lg"
                >
                  {t('Explore My Work')}
                </Button>
              </div>
            </div>
          </CarouselItem>

          {/* Website Slide */}
          <CarouselItem className="h-full">
            <div className="relative h-full flex items-center justify-center">
              <div className="absolute inset-0 z-0">
                <img 
                  src={imageLoadErrors[backgroundImages[2]] 
                    ? getFallbackImage(2) 
                    : backgroundImages[2]
                  }
                  alt="Website development background"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50"></div>
              </div>

              <div className="relative z-10 text-center text-white px-6">
                <div className="flex justify-center mb-6 space-x-4">
                  <div className="inline-flex items-center justify-center bg-primary/20 text-primary rounded-full p-3">
                    <Laptop className="h-8 w-8" />
                  </div>
                  <div className="inline-flex items-center justify-center bg-primary/20 text-primary rounded-full p-3">
                    <Smartphone className="h-8 w-8" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
                  {t('Modern Websites')}
                </h1>
                <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto animate-fade-in" style={{animationDelay: "0.2s"}}>
                  {t('Responsive websites that work perfectly on all devices')}
                </p>
                <Button 
                  onClick={onExploreClick}
                  className="animate-fade-in"
                  style={{animationDelay: "0.4s"}}
                  size="lg"
                >
                  {t('Explore My Work')}
                </Button>
              </div>
            </div>
          </CarouselItem>
        </CarouselContent>
        
        <CarouselPrevious className="left-4 z-20" />
        <CarouselNext className="right-4 z-20" />
      </Carousel>

      {/* Slide indicators and down arrow positioned in the center */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-20 flex flex-col items-center">
        <div className="flex space-x-2 mb-6">
          {[0, 1, 2].map((index) => (
            <button
              key={index}
              onClick={() => {
                if (carouselRef.current?.api) {
                  carouselRef.current.api.scrollTo(index);
                }
              }}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                activeIndex === index ? "bg-white scale-110" : "bg-white/50"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        <div 
          className={`cursor-pointer transition-opacity duration-500 ${headerAnimationComplete ? 'opacity-100 animate-bounce' : 'opacity-0'}`}
          onClick={onExploreClick}
        >
          <ChevronDown className="w-10 h-10 text-white" />
        </div>
      </div>
    </div>
  );
};
export default HeroSlider;